import { App } from '../AppManager';

interface HTMLGeneratorOptions {
  appData: App;
  customDomain?: string;
}

export const generateHTML = ({ appData, customDomain }: HTMLGeneratorOptions): string => {
  // Get the primary domain (custom domain or Firebase domain)
  const primaryDomain = customDomain || `${appData.id}.web.app`;
  const canonicalUrl = `https://${primaryDomain}`;
  
  // Determine theme color
  let themeColor = '#4169e1'; // Default accent color
  if (appData.theme) {
    if (appData.theme.custom) {
      // Use topbarBackgroundColor for custom themes
      themeColor = appData.theme.custom.topbarBackgroundColor;
    } else {
      // Use accent color for default themes
      themeColor = appData.theme.accentColor;
    }
  }

  // Generate app description
  const appDescription = `Welcome to ${appData.name}, your premier ordering destination. Enjoy our delightful selection of cuisine, crafted with passion by our expert chefs.`;
  
  // Generate keywords based on app name and type
  const keywords = `${appData.name}, restaurant, cuisine, dining, online ordering, food delivery, takeout, food, drinks, ${appData.type.replace('-', ' ')}`;

  const htmlContent = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<link rel="icon" href="%PUBLIC_URL%/favicon.png" />
<link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/4.7.0/css/font-awesome.min.css" />
<meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover">
<meta
name="Dine Platform - ${appData.name}"
content="Dine Platform - ${appData.name}"
/>
<meta
name="${appData.name}"
content="${appData.name}"
/>
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<meta name="description" content="${appDescription}">
<meta name="keywords" content="${keywords}">
<meta name="author" content="${appData.name}">
<meta name="robots" content="index, follow">
<!-- Open Graph Meta Tags for Social Media -->
<meta property="og:title" content="${appData.name} - Dine Platform">
<meta property="og:description" content="Order online with ${appData.name}">
<meta property="og:image" content="%PUBLIC_URL%/metaimg.png">
<meta property="og:url" content="${canonicalUrl}">
<link rel="canonical" href="${canonicalUrl}">
<meta property="og:type" content="restaurant">
<!-- Twitter Card Meta Tags -->
<meta name="twitter:card" content="summary_large_image">
<meta name="twitter:title" content="${appData.name}">
<meta name="twitter:description" content="Join us at ${appData.name} for an unforgettable culinary experience.">
<meta name="twitter:image" content="%PUBLIC_URL%/metaimg.png">
<link rel="apple-touch-icon" href="%PUBLIC_URL%/favicon.png" />
<link rel="manifest" href="%PUBLIC_URL%/manifest.json" />
<meta name="theme-color" content="${themeColor}">
<!-- For Windows Phone -->
<meta name="msapplication-navbutton-color" content="${themeColor}">
<script type="application/ld+json">
 {
 "@context": "https://schema.org",
 "@type": "WebSite",
 "name": "${appData.name} Online Ordering",
 "url": "${canonicalUrl}",
 "provider": {
 "@type": "Organization",
 "name": "Dine Platform",
 "url": "https://www.dineplatform.com"
 }
 }
</script>
<title>${appData.name}</title>
</head>
<body id="body" style="background-color: grey;">
<noscript>You need to enable JavaScript to run this app.</noscript>
<div id="root" style="height: 100vh; height: 100svh;"></div>
<!-- Powered by Dine Platform -->
<div style="display: none;">
  <a href="https://www.dineplatform.com" rel="nofollow">Powered by Dine Platform</a>
</div>
</body>
</html>`;

  return htmlContent;
};

export const generateHTMLFile = (options: HTMLGeneratorOptions): { content: string; filename: string } => {
  const content = generateHTML(options);
  const filename = `index.html.${options.appData.id}`;
  
  return {
    content,
    filename
  };
};